import React, { useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, CssBaseline, ThemeProvider, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import Android from '@mui/icons-material/Android';
import EastIcon from '@mui/icons-material/East';

import { Capacitor } from '@capacitor/core';
import clsx from 'clsx';

import { ReactComponent as AppleIcon } from '../../../../assets/icons/apple.svg';
import { ReactComponent as ForceUpdateIcon } from '../../../../assets/icons/force_update.svg';
import { BODY_DARK_THEME_CLASS } from '../../../../context/ThemeContext';
import config from '../../../../envConfig';
import { useAppFile } from '../../../../hooks/use-app-file';
import { usePwaInstall } from '../../../../hooks/use-pwa-install';
import {
  useGetAppLinkUpdate,
  useGetAppVariables,
  useGetVersion,
} from '../../../../redux/api/appConfigApi';
import { UiAlpha } from '../../../../styles/ui-alpha';
import {
  AppVariableKeys,
  getAppVariableValueInsensitive,
} from '../../../../types/interfaces/app-variable';
import { isValidHttpUrl } from '../../../../utils/platform/is-valid-http-url';
import { openExternalUrl } from '../../../../utils/platform/open-external-url';
import { createAppThemeFromLocalStorage } from '../../../../utils/theme/create-app-theme-from-local-storage';
import AppAlert from '../../../designe/app-alert/AppAlert';
import AppButton from '../../../designe/buttons/app-button/AppButton';
import { useStyles } from './UpdateRequiredPage.styles';

const ICON_SIZE = 28;
const ICON_SQUARE_SIZE = 48;

function UpdateRequiredPageInner() {
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const { t } = useTranslation();
  const { data: appLinkData } = useGetAppLinkUpdate();
  const { data: versionInfo } = useGetVersion();
  const { data: variables } = useGetAppVariables();
  const { downloadFile, isDownloading } = useAppFile(t);
  const { isPwaInstalled, reloadPwa } = usePwaInstall();
  const apkFileName =
    getAppVariableValueInsensitive(variables, AppVariableKeys.apkFileName) ??
    'SportGround.apk';
  const iosBuildLink = getAppVariableValueInsensitive(
    variables,
    AppVariableKeys.iosBuildLink,
  );
  const hasValidIosBuildLink = isValidHttpUrl(iosBuildLink);
  const isNativePlatform = Capacitor.getPlatform() !== 'web';
  const displayedCurrentVersion = isNativePlatform
    ? config.version
    : versionInfo?.currentVersion;
  const backendVersion = versionInfo?.currentVersion;
  const errorMain = theme.palette.error.main;

  return (
    <div className={classes.container}>
      <CssBaseline />
      <div className={classes.content}>
        <Box
          className={classes.iconSquare}
          sx={{
            backgroundColor: alpha(errorMain, UiAlpha.Tint),
            color: errorMain,
            width: ICON_SQUARE_SIZE,
            height: ICON_SQUARE_SIZE,
          }}>
          <ForceUpdateIcon
            className={classes.errorImage}
            style={{ width: ICON_SIZE, height: ICON_SIZE }}
            aria-hidden
          />
        </Box>
        <div className={classes.mainContent}>
          <Typography variant="h2" className={classes.pageTitle}>
            {t('updateRequired.title')}
          </Typography>
          <Typography variant="text-md" className={classes.pageDescription}>
            {t('updateRequired.description')}
          </Typography>
          <Box className={classes.block}>
            <Box className={classes.versionCompareRow}>
              <Box className={classes.versionBadgeError}>
                <Typography
                  variant="text-md-sb"
                  color="inherit"
                  className={clsx(
                    classes.versionValue,
                    classes.versionStrikethrough,
                  )}>
                  {displayedCurrentVersion ?? '—'}
                </Typography>
              </Box>
              <EastIcon className={classes.versionArrow} aria-hidden />
              <Box className={classes.versionBadgePrimary}>
                <Typography
                  variant="text-md-sb"
                  color="inherit"
                  className={classes.versionValue}>
                  {backendVersion ?? '—'}
                </Typography>
              </Box>
            </Box>
            <AppAlert severity="error" className={classes.warningAlert}>
              <Typography variant="text-sm" component="div" color="error">
                <Typography
                  variant="text-sm-sb"
                  color="error"
                  marginRight={0.5}>
                  {t('updateRequired.criticalWarningTitle')}
                </Typography>
                {t('updateRequired.criticalWarning')}
              </Typography>
            </AppAlert>
            {appLinkData?.link && (
              <AppButton
                variant="contained"
                color="primary"
                fullWidth
                className={classes.updateButton}
                loading={isDownloading}
                icon={<Android className={classes.buttonIcon} />}
                onClick={() =>
                  downloadFile(appLinkData.link, apkFileName, {
                    successMessageKey: 'messages.fileDownloaded',
                  })
                }>
                {t('versionUpdate.downloadUpdate')}
              </AppButton>
            )}
            {hasValidIosBuildLink && (
              <AppButton
                variant="contained"
                color="primary"
                fullWidth
                className={classes.updateButton}
                icon={<AppleIcon className={classes.buttonIcon} />}
                onClick={() => void openExternalUrl(iosBuildLink)}>
                {t('versionUpdate.openIosBuild')}
              </AppButton>
            )}
            {isPwaInstalled && (
              <AppButton
                variant="contained"
                color="primary"
                fullWidth
                className={classes.updateButton}
                onClick={reloadPwa}>
                {t('versionUpdate.updatePwa')}
              </AppButton>
            )}
          </Box>
        </div>
      </div>
    </div>
  );
}

export default function UpdateRequiredPage() {
  const theme = useMemo(() => createAppThemeFromLocalStorage(), []);

  useLayoutEffect(() => {
    if (theme.palette.mode === 'dark') {
      document.body.classList.add(BODY_DARK_THEME_CLASS);
      document.documentElement.classList.add(BODY_DARK_THEME_CLASS);
    } else {
      document.body.classList.remove(BODY_DARK_THEME_CLASS);
      document.documentElement.classList.remove(BODY_DARK_THEME_CLASS);
    }
    return () => {
      document.body.classList.remove(BODY_DARK_THEME_CLASS);
      document.documentElement.classList.remove(BODY_DARK_THEME_CLASS);
    };
  }, [theme]);

  return (
    <ThemeProvider theme={theme}>
      <UpdateRequiredPageInner />
    </ThemeProvider>
  );
}
